import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-300 font-sans">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3"
            >
              <h1 className="text-2xl font-bold text-white font-poppins">
                BLACKBOX<span className="text-purple-500">mi</span>
              </h1>
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex justify-center items-center p-3 bg-purple-500/10 rounded-full mb-6">
                <Shield className="w-8 h-8 text-purple-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-poppins">Privacy Policy</h1>
            <p className="text-lg text-zinc-500">Last Updated: November 20, 2025</p>
          </div>

          <div className="prose prose-invert prose-lg max-w-none space-y-8 text-zinc-300">
            <p>BLACKBOXmi (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;), operated by <strong>BLACKLIGHT Films/BLACKBOXis</strong>, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our film marketing intelligence platform and related AI services (the &ldquo;Service&rdquo;).</p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <div className="pl-4 border-l-2 border-zinc-800">
                <h3 className="text-xl font-medium text-purple-400 my-3">Information You Provide:</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Data:</strong> Name, email address, and password.</li>
                  <li><strong>Project Data:</strong> Film titles, loglines, scripts, genres, budgets, and cast lists you submit for analysis.</li>
                  <li><strong>Payment Data:</strong> Transaction details processed securely via Stripe. We do not store full credit card numbers.</li>
                  <li><strong>Generated Content:</strong> Press kits, ad concepts, and visual assets generated by our AI tools on your behalf.</li>
                </ul>

                <h3 className="text-xl font-medium text-purple-400 my-3">Automated Collection:</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Usage Analytics:</strong> Features used, reports generated, and credit consumption history.</li>
                  <li><strong>Device Data:</strong> IP address, browser type, and operating system to ensure security and performance.</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p>We leverage your data primarily to power our AI analysis engines:</p>
               <ul className="list-disc pl-6 space-y-2">
                <li>To generate Audience Persona reports, Revenue Forecasts, and Budget Plans.</li>
                <li>To create AI-generated marketing assets (Posters, Press Kits, Ad Copy).</li>
                <li>To process credit usage and subscription billing.</li>
                <li>To improve the accuracy of our forecasting algorithms (using anonymized, aggregated data).</li>
                <li>To send transactional emails (PDF exports, receipts, account alerts).</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. AI & Third-Party Sharing</h2>
              <p>To provide our services, we interface with trusted third-party providers:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>LLM & AI Providers:</strong> Text and script data may be processed by enterprise-grade AI providers (e.g., OpenAI) to generate analyses. Data is not used to train public models.</li>
                <li><strong>Payment Processing:</strong> Stripe handles all billing securely.</li>
                <li><strong>Cloud Infrastructure:</strong> Data is hosted on secure servers (Firebase/Vercel).</li>
                <li><strong>No Sale of Data:</strong> We do not sell your personal information or specific film data to advertisers.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Data Security</h2>
              <p>We employ industry-standard encryption (SSL/TLS) for data in transit and at rest. However, no internet transmission is 100% secure. You are responsible for keeping your login credentials confidential.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Retention</h2>
              <p>We retain project data while your account is active to allow you to access past reports. Upon account cancellation, data is retained for 30 days for recovery purposes before being permanently deleted.</p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Contact Us</h2>
              <p>For privacy concerns, please contact our parent company:</p>
              <div className="mt-4 p-6 bg-zinc-900 rounded-xl border border-zinc-800">
                <p className="mb-2"><strong>BLACKLIGHT Films / BLACKBOXis</strong></p>
                <p className="mb-2"><strong>Email:</strong> <a href="mailto:support@blackbox-is.com" className="text-purple-400 hover:underline">support@blackbox-is.com</a></p>
              </div>
            </section>
          </div>

          <div className="mt-12 text-center pb-12">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Return to Home
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;